import { action, computed, makeObservable, observable, reaction } from 'mobx'
import { type FirebaseRepository } from '../../models/FirebaseRepository'
import { ChatController } from '../meeting/ChatController'
import type { MeetingCubit } from '../MeetingCubit'
import { sendNudge } from '../../firestore/RoomMessage'
import { captureException } from '@sentry/core'

export class MeetingChatController extends ChatController {
  private meeting: MeetingCubit

  /**
   * ID of the nudge message that is currently in progress (being sent)
   * Used to track the state of the nudge message until it is confirmed by the server
   * and we have representation of the write on the client
   */
  @observable
  private localNudgeInProgressID: string | undefined = undefined

  constructor({
    repository,
    roomId,
    isDevRoom,
    meeting,
  }: {
    repository: FirebaseRepository
    roomId: string
    isDevRoom?: boolean
    meeting: MeetingCubit
  }) {
    super({ repository, roomId, isDevRoom })
    this.meeting = meeting
    makeObservable(this)
  }

  @computed
  get missingUserIds() {
    const configurationParticipants = this.meeting.deviceConfigurationController
      ? this.meeting.deviceConfigurationController.connectedParticipantIds
      : []

    const presentUsers = [
      // populated after meeting connected
      ...this.meeting.participantIds,
      // populated during av check
      ...configurationParticipants,
      this.repository.uid, // Always include current user
    ]

    const expectedUsers = this.meeting.roomState.data.userIds
    return expectedUsers.filter((userId) => !presentUsers.includes(userId))
  }

  @computed
  get userIdsWhichCanBeNudged() {
    const missingUserIds = this.missingUserIds
    const recentlyNudgedUsers = this.recentlyNudgedUsers
    return missingUserIds.filter((userId) => !recentlyNudgedUsers.has(userId))
  }

  @computed
  get recentNudgeMessages() {
    return this.messagesCol.models.filter((message) => {
      // if not a successful or in flight nude message, skip
      if (message.data.type !== 'nudge' || message.data.nudgeSent === false) {
        return false
      }
      const twoHoursAgo = Date.now() - 2 * 60 * 60 * 1000
      return message.data.createdAt.getTime() >= twoHoursAgo
    })
  }

  @computed
  private get recentlyNudgedUsers() {
    const nudgedUserIds = new Set<string>()
    for (const {
      data: { metadata },
    } of this.recentNudgeMessages) {
      if (!metadata || metadata.type !== 'nudge') continue
      metadata.userIds.forEach((userId) => nudgedUserIds.add(userId))
    }
    return nudgedUserIds
  }

  @action
  private setLocalNudgeInProgressID(id: string | undefined) {
    this.localNudgeInProgressID = id
  }

  async sendNudge(): Promise<{ didError: boolean }> {
    if (!this.userIdsWhichCanBeNudged.length) return { didError: false }
    try {
      const resp = await sendNudge(
        this.firestore,
        this.roomId,
        this.repository.uid,
        this.userIdsWhichCanBeNudged
      )

      // get the id from the message and set the local id
      this.setLocalNudgeInProgressID(resp.id)

      const disposeListener = reaction(
        () => this.messagesCol.models,
        (messages) => {
          const messageMatchingId = messages.find(
            (m) => m.id === this.localNudgeInProgressID
          )

          // trigger has not finished yet
          if (
            !messageMatchingId ||
            messageMatchingId.data.nudgeSent === undefined
          ) {
            return
          }

          // trigger has finished, unset local in progress id
          // and dispose reaction
          this.setLocalNudgeInProgressID(undefined)
          disposeListener()
        },
        {
          fireImmediately: true,
        }
      )
      // kill this thing when the cubit dies
      this.disposers.push(() => disposeListener())
      return { didError: false }
    } catch (e) {
      captureException(e)
      return { didError: true }
    }
  }

  @computed
  get nudgeIsInProgress() {
    // this will set before the write is confirmed when we get the server resp, trust this always
    if (this.localNudgeInProgressID) return true

    // else check messages to see if we have one sent, the above state will no persist across refreshes etc.
    // it's just there for a snappier signal at the UI level, send -> loading state instantly
    // a bit jarring if the write fails but that should rare enough to be acceptable
    const inProgressNudges = this.recentNudgeMessages.filter(
      (msg) => msg.data.nudgeSent === undefined
    )
    // trigger is still handling
    return inProgressNudges.length > 0
  }
}
